import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { hubSignupHref } from '../lib/hub-signup'
import { safeNextPath } from '../lib/safe-next'
import { supabase } from '../lib/supabase'
import { authCaptchaOptions, captchaErrorMessage, prewarmAuthChallenge } from '../lib/turnstile'

// Every GoTrue call below carries a Turnstile token when one is configured (lib/turnstile.ts). That
// is not this product's own anti-abuse measure: the captcha switch lives on the SHARED Supabase
// project, so when it is turned on for TrackingMCP it is on here too, and a call without a token is
// refused outright. With no site key set, authCaptchaOptions() returns {} and nothing changes.
//
// SchedulesMCP shares TrackingMCP's Supabase project, so the identity store (auth.users) is the
// same, so a TrackingMCP user signs in here with the SAME Google/GitHub/email account, no migration.
// The providers below are the ones enabled+in-use on the shared project (google, github, email).
type OAuthProvider = 'google' | 'github'

export function LoginPage() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const next = safeNextPath(params.get('next'))
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Start the challenge as soon as the page opens, not when the button is pressed. The deadline is
  // thirty seconds because a slow widget has taken fourteen; running it while the person types their
  // address means nobody waits for it, and a slow challenge still gets its full budget.
  useEffect(() => {
    prewarmAuthChallenge()
  }, [])

  const redirectTo = `${window.location.origin}${next}`

  // VITE_HUB_SIGNUP=1: "Sign up" goes to the hub sign-up instead of the local form. Null (the default,
  // or a host that is not this site's own https origin) keeps the local form exactly as it was.
  const hubSignup = hubSignupHref(next)

  async function oauth(provider: OAuthProvider) {
    setBusy(true)
    setMsg(null)
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo } })
    // On success the browser is redirected to the provider; only errors land here.
    if (error) {
      setMsg(error.message)
      setBusy(false)
    }
  }

  async function magicLink() {
    if (!email) {
      setMsg('Enter your email first, then tap the magic link.')
      return
    }
    setBusy(true)
    setMsg(null)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo, ...(await authCaptchaOptions()) },
    })
    setMsg(
      error ? (captchaErrorMessage(error) ?? error.message) : 'Magic link sent. Check your email.'
    )
    setBusy(false)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: redirectTo, ...(await authCaptchaOptions()) },
        })
        if (error) throw error
        // Signing up an address that already has an account is not an error: with email-enumeration
        // protection on, Supabase returns a decoy user with NO identities and sends a confirmation
        // mail anyway. Telling that customer to "check your email to confirm" sends them back into
        // the sign-up flow they already completed, which is what one reported. Name it instead.
        if (data.user && data.user.identities?.length === 0) {
          setMsg('You already have a Navo24 account with this email. Sign in below.')
          setMode('signin')
        } else {
          setMsg('Check your email to confirm, then sign in.')
          setMode('signin')
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: { ...(await authCaptchaOptions()) },
        })
        if (error) throw error
        nav(next)
      }
    } catch (err) {
      // A captcha refusal reads as "captcha protection: request disallowed", which tells a customer
      // nothing and looks like the product is broken. Say what happened and name a route that works.
      setMsg(captchaErrorMessage(err as { message?: string }) ?? (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const oauthBtn =
    'w-full flex items-center justify-center gap-2.5 bg-surface-1 border border-border rounded-lg px-4 py-2.5 font-semibold text-[var(--text-primary)] hover:border-signal disabled:opacity-50'

  return (
    <main className="max-w-sm mx-auto px-6 pt-20 pb-16">
      <h1
        className="font-display text-3xl mb-2 text-center"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {mode === 'signin' ? 'Sign in' : 'Create account'}
      </h1>
      <p className="text-sm text-[var(--text-secondary)] text-center mb-8">
        Your Navo24 account works here and on TrackingMCP: same login.
      </p>

      {/* Ecosystem SSO: same identity as TrackingMCP */}
      <div className="space-y-2.5">
        <button type="button" onClick={() => oauth('google')} disabled={busy} className={oauthBtn}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.1a6.6 6.6 0 0 1 0-4.22V7.04H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
            />
          </svg>
          Continue with Google
        </button>
        <button type="button" onClick={() => oauth('github')} disabled={busy} className={oauthBtn}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 1A11 11 0 0 0 8.5 22.43c.55.1.75-.24.75-.53v-1.86c-3.06.67-3.7-1.48-3.7-1.48-.5-1.27-1.22-1.6-1.22-1.6-1-.69.08-.67.08-.67 1.1.08 1.68 1.13 1.68 1.13.98 1.69 2.57 1.2 3.2.92.1-.71.38-1.2.7-1.48-2.45-.28-5.02-1.22-5.02-5.45 0-1.2.43-2.18 1.13-2.95-.11-.28-.49-1.4.11-2.92 0 0 .92-.3 3.02 1.13a10.4 10.4 0 0 1 5.5 0c2.1-1.43 3.02-1.13 3.02-1.13.6 1.52.22 2.64.11 2.92.7.77 1.13 1.75 1.13 2.95 0 4.24-2.58 5.17-5.03 5.44.39.34.74 1 .74 2.03v3.01c0 .29.2.64.76.53A11 11 0 0 0 12 1Z" />
          </svg>
          Continue with GitHub
        </button>
      </div>

      <div className="flex items-center gap-3 my-6">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-[var(--text-secondary)]">or with email</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-surface-1 border border-border rounded-lg px-3 py-2.5 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <input
          type="password"
          required
          minLength={8}
          placeholder="Password (8+ characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-surface-1 border border-border rounded-lg px-3 py-2.5 text-[var(--text-primary)] focus:border-signal outline-none"
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-signal text-on-accent font-bold px-4 py-2.5 rounded-lg disabled:opacity-50"
        >
          {busy ? '…' : mode === 'signin' ? 'Sign in' : 'Sign up'}
        </button>
      </form>

      <button
        type="button"
        onClick={magicLink}
        disabled={busy}
        className="block mx-auto mt-3 text-sm text-[var(--text-secondary)] hover:text-signal"
      >
        Email me a magic link instead
      </button>

      {msg && <p className="text-sm text-warn mt-4 text-center">{msg}</p>}
      {hubSignup ? (
        <a
          href={hubSignup}
          className="block mx-auto mt-6 text-sm text-[var(--text-secondary)] hover:text-signal"
        >
          Need an account? Continue with Navo24 account
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
          className="block mx-auto mt-6 text-sm text-[var(--text-secondary)] hover:text-signal"
        >
          {mode === 'signin' ? 'Need an account? Sign up' : 'Have an account? Sign in'}
        </button>
      )}
    </main>
  )
}
